import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Pickaxe, 
  Sprout, 
  Building2, 
  Zap, 
  Radio, 
  Building, 
  Shield, 
  Briefcase, 
  Target,
  ArrowRight,
  TrendingUp,
  ChevronRight,
  Play,
  Users,
  ChevronLeft
} from 'lucide-react';
import { mockData } from '../mockData';
import VocalAvatar from '../components/VocalAvatar';

const iconMap = {
  Pickaxe, Sprout, Building2, Zap, Radio, Building, Shield, Briefcase, Target
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.8 }
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const HomePage = () => {
  const { t } = useTranslation();
  const [playingVideo, setPlayingVideo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Premium DRC Images for carousel
  const heroSlides = [
    {
      image: mockData.drcImages.city1,
      title: "Modern Infrastructure",
      subtitle: "Leading Africa's Development"
    },
    {
      image: mockData.drcImages.city2,
      title: "Urban Excellence",
      subtitle: "Gateway to Investment"
    },
    {
      image: mockData.drcImages.infrastructure,
      title: "Strategic Projects",
      subtitle: "Building Tomorrow"
    },
    {
      image: "https://images.unsplash.com/photo-1689489608113-107f10b63d5e?w=1200&q=90",
      title: "Natural Resources",
      subtitle: "Powering Global Growth"
    },
    {
      image: "https://images.unsplash.com/photo-1630770131048-375099b304ed?w=1200&q=90",
      title: "Renewable Energy",
      subtitle: "Sustainable Future"
    }
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000); // Change slide every 10 seconds (5s transition + 5s viewing)
    
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Vocal Avatar */}
      <VocalAvatar />

      {/* Premium Hero Section with Carousel */}
      <section className="relative h-screen min-h-[700px] overflow-hidden">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
            />
            {/* Dark overlay for text readability - even lighter for maximum image visibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/75 via-blue-900/60 to-blue-900/50"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content Container */}
        <div className="relative h-full flex items-center pt-24 sm:pt-28 md:pt-32 lg:pt-36">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div 
                className="space-y-4 md:space-y-8 text-white"
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-4 md:px-6 py-1.5 md:py-2 text-xs md:text-sm font-semibold">
                    🌍 {t('home.gateway')}
                  </Badge>
                </motion.div>
                
                <motion.h1 
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight"
                  variants={fadeInUp}
                >
                  {t('home.title')}{' '}
                  <span className="text-yellow-400">
                    {t('home.drc')}
                  </span>
                </motion.h1>
                
                <motion.p 
                  className="text-lg sm:text-xl md:text-2xl text-blue-100 leading-relaxed font-light"
                  variants={fadeInUp}
                >
                  {t('home.tagline')}
                </motion.p>
                
                <motion.p 
                  className="text-base md:text-lg text-blue-50 leading-relaxed"
                  variants={fadeInUp}
                >
                  {t('home.trustedPartner')}
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2 md:pt-4"
                  variants={fadeInUp}
                >
                  <Link to="/business-areas" className="w-full sm:w-auto">
                    <Button 
                      size="lg" 
                      className="bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-10 py-5 md:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 group w-full"
                    >
                      {t('home.exploreOpportunities')}
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/contact" className="w-full sm:w-auto">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm text-base md:text-lg px-6 md:px-10 py-5 md:py-7 font-semibold w-full"
                    >
                      {t('common.getInTouch')}
                    </Button>
                  </Link>
                </motion.div>
                
                {/* Slide Indicators */}
                <motion.div 
                  className="flex gap-2 md:gap-3 pt-2 md:pt-4"
                  variants={fadeInUp}
                >
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'w-12 bg-white' 
                          : 'w-8 bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </motion.div>
              </motion.div>
              
              {/* Right Side - Slide Info Card */}
              <motion.div 
                className="relative hidden lg:block"
                initial="hidden"
                animate="visible"
                variants={slideInRight}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl"
                  >
                    <div className="space-y-4">
                      <div className="text-sm font-semibold text-blue-200 uppercase tracking-wider">
                        Featured Project
                      </div>
                      <h3 className="text-4xl font-bold text-white">
                        {heroSlides[currentSlide].title}
                      </h3>
                      <p className="text-xl text-blue-100">
                        {heroSlides[currentSlide].subtitle}
                      </p>
                      <div className="pt-4">
                        <Link to="/service-details">
                          <Button 
                            variant="ghost" 
                            className="text-white hover:bg-white/20 group"
                          >
                            Learn More 
                            <ChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20 z-10"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20 z-10"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </motion.div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.statistics.map((stat, index) => (
              <div key={index} className="text-center space-y-3 group">
                <div className="text-5xl md:text-6xl font-bold group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-xl font-semibold text-blue-200">{stat.label}</div>
                <div className="text-sm text-blue-300">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Areas Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <Badge className="bg-blue-100 text-blue-900 px-6 py-2 text-sm font-semibold">
              <TrendingUp className="w-4 h-4 mr-2" />
              {t('home.investmentSectors')}
            </Badge>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900">{t('home.featuredBusinessAreas')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('home.discoverOpportunities')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.businessAreas.slice(0, 3).map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <Card 
                  key={area.id}
                  className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-blue-500 overflow-hidden cursor-pointer transform hover:-translate-y-2"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={area.image}
                      alt={area.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="bg-white p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                        <IconComponent className="w-7 h-7 text-blue-900" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-900 group-hover:text-blue-700 transition-colors">
                      {t(`home.${area.title.toLowerCase().replace(/\s+/g, '')}`)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-base line-clamp-3">
                      {t(`home.${area.title.toLowerCase().replace(/\s+/g, '')}Desc`)}
                    </CardDescription>
                    <Link to="/business-areas">
                      <Button 
                        variant="link" 
                        className="mt-4 p-0 text-blue-900 group-hover:text-blue-700 font-semibold text-base"
                      >
                        Learn More <ChevronRight className="ml-1 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/business-areas">
              <Button size="lg" className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                View All Business Areas
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* DRC Showcase with Images */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Discover the Democratic Republic of Congo
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A land of opportunity with vibrant cities and rich natural resources
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src={mockData.drcImages.city1}
                alt="Kinshasa"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Kinshasa - The Capital</h3>
                  <p className="text-blue-200">Modern infrastructure and business opportunities</p>
                </div>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src={mockData.drcImages.city2}
                alt="DRC Development"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Growing Economy</h3>
                  <p className="text-blue-200">Strategic location for African business</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '50px 50px'}}></div>
        </div>
        <div className="max-w-4xl mx-auto text-center space-y-8 relative">
          <h2 className="text-5xl md:text-6xl font-bold">
            Partner with <span className="text-blue-300">OL CONSULT</span> Today
          </h2>
          <p className="text-2xl text-blue-100 font-light">
            Unlock the immense potential of the Democratic Republic of Congo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-12 py-7 shadow-2xl group font-semibold"
              >
                Get Started Now
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-12 py-7 font-semibold"
              >
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
